/**
 * "The dashboard" overview — Dashboard section (ADR-0074).
 *
 * Reconciled from apps/web (`core/AppShell/sidebar-definitions*.ts`, the
 * workspace/environment switchers, the user menu, and the home page).
 *
 * ## Why the navigation section is a full index
 *
 * It used to describe three groups — Agent, Surfaces, Platform — and the
 * product has had seven for some time, so the one page whose job is orientation
 * was sending readers to look for headings that are not there. It now lists
 * every group with the surfaces inside it, which also makes this page the index
 * for the ones without a page of their own.
 */
import type { DocPageContent } from '../types';

export const DASHBOARD_OVERVIEW_PAGE: DocPageContent = {
  slug: 'dashboard',
  titleKey: 'docs.dashboard.overview.title',
  descriptionKey: 'docs.dashboard.overview.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.lead' },

    { kind: 'heading', level: 2, id: 'navigation', textKey: 'docs.dashboard.overview.nav.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.nav.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.dashboard.overview.group.agent.term',
          descKey: 'docs.dashboard.overview.group.agent.desc',
        },
        {
          termKey: 'docs.dashboard.overview.group.surfaces.term',
          descKey: 'docs.dashboard.overview.group.surfaces.desc',
        },
        {
          termKey: 'docs.dashboard.overview.group.observability.term',
          descKey: 'docs.dashboard.overview.group.observability.desc',
        },
        {
          termKey: 'docs.dashboard.overview.group.limits.term',
          descKey: 'docs.dashboard.overview.group.limits.desc',
        },
        {
          termKey: 'docs.dashboard.overview.group.connect.term',
          descKey: 'docs.dashboard.overview.group.connect.desc',
        },
        {
          termKey: 'docs.dashboard.overview.group.org.term',
          descKey: 'docs.dashboard.overview.group.org.desc',
        },
      ],
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.nav.home' },

    {
      kind: 'heading',
      level: 2,
      id: 'workspaces',
      textKey: 'docs.dashboard.overview.workspace.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.workspace.body' },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.dashboard.overview.workspace.note.title',
      textKey: 'docs.dashboard.overview.workspace.note.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'account',
      textKey: 'docs.dashboard.overview.account.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.account.body' },

    { kind: 'heading', level: 2, id: 'setup', textKey: 'docs.dashboard.overview.setup.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.overview.setup.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.overview.next.catalog.title',
          textKey: 'docs.dashboard.overview.next.catalog.text',
          to: { docSlug: 'dashboard/catalog' },
        },
        {
          titleKey: 'docs.dashboard.overview.next.prompts.title',
          textKey: 'docs.dashboard.overview.next.prompts.text',
          to: { docSlug: 'dashboard/prompts' },
        },
      ],
    },
  ],
};
